import React from 'react'
import { Table, Tooltip, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import styled from 'styled-components'
import { t } from '@/i18n'
import { TemplateItem, TEMPLATE_TYPES } from '../../types/template'
import { BasSwitch } from '@/components'
import Scrollbars from 'react-custom-scrollbars'
import { TypeTag } from '@/pages/program/temporary/widgets'
import { createFilterColumn } from '@/components/filter-dropdown-select'
import { formatDateTime } from '@/utils/date'

const { Text } = Typography

interface TemplateTableProps {
  data: TemplateItem[]
  loading: boolean
  pagination: {
    current: number
    pageSize: number
    total: number
  }
  selectedKeys: string[]
  executeUpdateKeys: string[]
  isLatest: boolean
  templateUseCaseCount: Record<string, number | string>
  onSelectionChange: (templateId: string, selected: boolean) => void
  onExecuteUpdateChange: (templateId: string, enabled: boolean) => void
  onTableChange: (pagination: any, filters: any, sorter: any) => void
  onTableSelectChange: (selectedRowKeys: React.Key[]) => void
}

const TemplateTable: React.FC<TemplateTableProps> = ({
  data,
  loading,
  pagination,
  selectedKeys,
  executeUpdateKeys,
  isLatest,
  templateUseCaseCount,
  onSelectionChange,
  onExecuteUpdateChange,
  onTableChange,
  onTableSelectChange,
}) => {
  const columns: ColumnsType<TemplateItem> = [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left',
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ maxWidth: 150 }}
        >
          {text}
        </Text>
      ),
    },
    createFilterColumn({
      title: t('模板类型'),
      dataIndex: 'originType',
      key: 'originType',
      width: 120,
      options: [...TEMPLATE_TYPES],
      placeholder: t('请选择模板类型'),
      render: (originType: string) => <TypeTag type={originType} />,
    }),
    {
      title: t('执行更新'),
      dataIndex: 'executeUpdate',
      key: 'executeUpdate',
      width: 100,
      render: (_, record) => {
        // 判断是否选中了该模板
        const isTemplateSelected = selectedKeys.includes(record.templateId)

        // 判断该模板是否全选了用例
        const isAllSelected = templateUseCaseCount.isAll === 'isAll'
        const selectedCount = isAllSelected
          ? record.resourceAmount
          : (templateUseCaseCount[record.templateId] as number) || 0
        const isAllUseCasesSelected = selectedCount === record.resourceAmount

        // 判断是否可以操作：模板被选中 且 全部用例都选中
        const canOperate = isTemplateSelected && isAllUseCasesSelected

        return (
          <BasSwitch
            checked={executeUpdateKeys.includes(record.templateId)}
            disabled={!isLatest || !canOperate}
            onChange={checked => onExecuteUpdateChange(record.templateId, checked)}
          />
        )
      },
    },
    {
      title: t('全部 | 已选用例数'),
      dataIndex: 'resourceAmount',
      key: 'resourceAmount',
      width: 150,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (_, record) => {
        // 判断是否为全选状态
        const isAllSelected = templateUseCaseCount.isAll === 'isAll'
        const selectedCount = isAllSelected
          ? record.resourceAmount
          : (templateUseCaseCount[record.templateId] as number) || 0

        return (
          <UseCaseCount>
            {record.resourceAmount} | {selectedCount}
          </UseCaseCount>
        )
      },
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 120,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: onTableSelectChange,
    onSelect: (record: TemplateItem, selected: boolean) => {
      onSelectionChange(record.templateId, selected)
    },
    onSelectAll: (selected: boolean) => {
      data.forEach(record => {
        onSelectionChange(record.templateId, selected)
      })
    },
  }

  return (
    <div style={{ height: 550, marginTop: 8 }}>
      <Scrollbars>
        <StyledTable
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="templateId"
          rowSelection={rowSelection}
          onChange={onTableChange}
          pagination={{
            total: pagination.total,
            showTotal: total => (
              <span>
                {t('共')}
                <span style={{ color: '#4238c9' }}>{total}</span>
                {t('条')}
              </span>
            ),
            pageSize: pagination.pageSize,
            current: pagination.current,
            showQuickJumper: true,
            showSizeChanger: true,
          }}
          scroll={{ x: 300 }}
        />
      </Scrollbars>
    </div>
  )
}

export default TemplateTable

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 500;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }

  .ant-table-measure-row {
    display: none;
  }
`

const UseCaseCount = styled.span`
  font-size: 12px;
  color: #666;
`
